/**
 * BulkEditPalletsModal.jsx
 *
 * Dedicated component for bulk editing pallets in the warehouse management system
 * Supports multi-select dropdown for empty pallets and form validation
 */

import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useAlert } from "../../providers/AlertProvider";
import { bulkEditPallets, bulkDeletePallets } from "../../api/enhancedWarehouse";
import logger from "../../utils/logger";

export default function BulkEditPalletsModal({
    isOpen,
    onClose,
    warehouseLanes = {},
    allPalletsData = [], // New pallet structure with occupancy data
    allCustomers = [],
    userProfile,
    triggerRefresh,
    onRefreshAllPallets, // Callback to refresh all pallets data
    theme = "light"
}) {
    const { showAlert } = useAlert();
    
    // State management
    const [selectedPalletIds, setSelectedPalletIds] = useState([]);
    const [palletSearchTerm, setPalletSearchTerm] = useState("");
    const [showPalletDropdown, setShowPalletDropdown] = useState(false);
    const [bulkEditForm, setBulkEditForm] = useState({
        pallet_type: "",
        notes: "",
        status: ""
    });
    const [isBulkEditing, setIsBulkEditing] = useState(false);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);

    // Ref for dropdown click-outside detection
    const palletDropdownRef = useRef(null);

    // Theme colors - match the main component exactly
    const isDark = theme === 'dark';
    const color = {
        bg: isDark ? "#080808" : "#fff",
        panel: isDark ? "#333" : "#fff",
        fg: isDark ? "#fff" : "#000",
        border: isDark ? "#fff" : "#000",
        headerBg: isDark ? "#2b2b2b" : "#eee",
        highlight: "rgba(26, 221, 247, 0.6)",
        success: "#28a745",
        warning: "#ffc107",
    };

    // Close modal and reset form
    const closeModal = () => {
        setSelectedPalletIds([]);
        setPalletSearchTerm("");
        setBulkEditForm({
            pallet_type: "",
            notes: "",
            status: ""
        });
        setShowPalletDropdown(false);
        onClose();
    };

    // Toggle pallet selection
    const togglePalletSelection = (palletId) => {
        setSelectedPalletIds(prev => 
            prev.includes(palletId) 
                ? prev.filter(id => id !== palletId)
                : [...prev, palletId]
        );
    };

    // Handle bulk edit submission
    const handleBulkEdit = async () => {
        if (selectedPalletIds.length === 0) return;
        
        if (!bulkEditForm.pallet_type) {
            showAlert({
                message: 'Please select a pallet type',
                type: 'warning',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
            return;
        }
        
        setIsBulkEditing(true);
        
        try {
            // Build updates object (notes is optional, others are required)
            const updates = {
                pallet_type: bulkEditForm.pallet_type
            };
            
            // Add optional fields if provided
            if (bulkEditForm.status) updates.status = bulkEditForm.status;
            
            const result = await bulkEditPallets(selectedPalletIds, updates);
            
            if (result.success) {
                showAlert({
                    message: result.message,
                    type: 'success',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
                await triggerRefresh(); // Refresh warehouse data
                
                // Also refresh all pallets data if callback is provided
                if (onRefreshAllPallets) {
                    await onRefreshAllPallets();
                }
                
                closeModal();
            } else {
                showAlert({
                    message: result.message,
                    type: 'danger',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
            }
        } catch (error) {
            logger.error('Error in bulk edit:', error);
            showAlert({
                message: 'Failed to update pallets',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        } finally {
            setIsBulkEditing(false);
        }
    };

    const handleBulkDelete = () => {
        if (selectedPalletIds.length === 0) return;
            showAlert({
                message: 'Are you sure you wish to DELETE selected pallets? This action cannot be undone.',
                type: 'warning',
                buttons: [
                    { label: 'Close',
                        type: 'primary'
                    },
                    { label: 'Delete',
                        type: 'danger',
                        onClick: () => deletePallets()
                    }
                ]
            });
            return;
    };

    // Handle bulk edit submission
    const deletePallets = async () => {
        setIsBulkDeleting(true);        

        try {
            const result = await bulkDeletePallets(selectedPalletIds);
            
            if (result.success) {
                showAlert({
                    message: result.message,
                    type: 'success',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
                await triggerRefresh(); // Refresh warehouse data
                
                // Also refresh all pallets data if callback is provided
                if (onRefreshAllPallets) {
                    await onRefreshAllPallets();
                }
                
                closeModal();
            } else {
                showAlert({
                    message: result.message,
                    type: 'danger',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
            }
        } catch (error) {
            logger.error('Error in bulk delete:', error);
            showAlert({
                message: 'Failed to delete pallets',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        } finally {
            setIsBulkDeleting(false);
        }
    };

    // Click outside to close pallet dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (palletDropdownRef.current && !palletDropdownRef.current.contains(event.target)) {
                setShowPalletDropdown(false);
            }
        };

        if (showPalletDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [showPalletDropdown]);

    // Debug: Log customers data


    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        }}>
            <div style={{
                backgroundColor: color.panel,
                border: `2px solid ${color.border}`,
                borderRadius: '8px',
                padding: '2rem',
                width: '90%',
                maxWidth: '600px',
                height: '85%',
                overflow: 'auto',
                color: color.fg
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1.5rem'
                }}>
                    <h3 style={{ margin: 0, color: color.fg }}>✏️ Bulk Edit Pallets</h3>
                    <button
                        onClick={closeModal}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '1.5rem',
                            cursor: 'pointer',
                            color: color.fg,
                            padding: '0.25rem'
                        }}
                    >
                        ×
                    </button>
                </div>

                {/* Pallet Selection */}
                <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontWeight: 'bold',
                        color: color.fg
                    }}>
                        Select Empty Pallets:
                    </label>
                    <div style={{ position: 'relative' }} ref={palletDropdownRef}>
                        <input
                            type="text"
                            value={palletSearchTerm}
                            onChange={(e) => setPalletSearchTerm(e.target.value)}
                            onFocus={() => setShowPalletDropdown(true)}
                            placeholder="Search pallets by code or warehouse..."
                            style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: `1px solid ${color.border}`,
                                borderRadius: '4px',
                                backgroundColor: color.bg,
                                color: color.fg,
                            }}
                        />

                        {/* Selected pallets display */}
                        {allPalletsData.length > 0 && (
                            <div style={{
                                marginTop: '0.5rem',
                                padding: '0.5rem',
                                backgroundColor: color.bg,
                                border: `1px solid ${color.border}`,
                                borderRadius: '4px'
                            }}>
                                <div style={{ fontSize: '0.85rem', marginBottom: '0.25rem', fontWeight: 'bold' }}>
                                    Selected: {selectedPalletIds.length} pallet(s)
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                                    {selectedPalletIds.map(palletId => {
                                        const pallet = allPalletsData.find(p => p.pallet_id === palletId);
                                        return pallet ? (
                                            <span key={palletId} style={{
                                                backgroundColor: color.highlight,
                                                color: '#000',
                                                padding: '0.125rem 0.375rem',
                                                borderRadius: '12px',
                                                fontSize: '0.75rem',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.25rem'
                                            }}>
                                                {pallet.pallet_code}
                                                <button
                                                    onClick={() => togglePalletSelection(palletId)}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        color: '#000',
                                                        cursor: 'pointer',
                                                        padding: 0,
                                                        fontSize: '0.75rem'
                                                    }}
                                                >
                                                    ✕
                                                </button>
                                            </span>
                                        ) : null;
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Dropdown */}
                        {showPalletDropdown && (
                            <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                maxHeight: '200px',
                                overflowY: 'auto',
                                backgroundColor: color.panel,
                                border: `1px solid ${color.border}`,
                                borderTop: 'none',
                                borderRadius: '0 0 4px 4px',
                                zIndex: 1000,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                            }}>
                                {/* Close dropdown button */}
                                <div style={{
                                    padding: '0.5rem',
                                    borderBottom: `1px solid ${color.border}`,
                                    backgroundColor: color.bg,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    fontWeight: 'bold',
                                    fontSize: '0.85rem'
                                }}>
                                    <span>Select Pallets</span>
                                    <button
                                        onClick={() => setShowPalletDropdown(false)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: '1rem',
                                            color: color.fg,
                                            padding: '0 0.25rem'
                                        }}
                                        title="Close dropdown"
                                    >
                                        ✕
                                    </button>
                                </div>

                                {/* All pallets in natural order with visual occupancy indicators */}
                                {allPalletsData
                                    .filter(pallet =>
                                        !palletSearchTerm.trim() ||
                                        pallet.pallet_code.toLowerCase().includes(palletSearchTerm.toLowerCase()) ||
                                        pallet.pallet_id.toLowerCase().includes(palletSearchTerm.toLowerCase())
                                    )
                                    //.sort((a, b) => a.pallet_code.localeCompare(b.pallet_code)) // Sort by pallet code
                                    .map(pallet => {
                                        const isOccupied = pallet.occupied;
                                        const isSelectable = !isOccupied;
                                        const isSelected = selectedPalletIds.includes(pallet.pallet_id);
                                        
                                        return (
                                            <div
                                                key={pallet.pallet_id}
                                                onClick={() => isSelectable ? togglePalletSelection(pallet.pallet_id) : null}
                                                style={{
                                                    padding: '0.75rem',
                                                    cursor: isSelectable ? 'pointer' : 'not-allowed',
                                                    borderBottom: `1px solid ${color.border}`,
                                                    backgroundColor: isSelected ? color.highlight : (isOccupied ? '#ffebeeec' : 'transparent'),
                                                    color: isOccupied ? '#d32f2f' : color.fg,
                                                    opacity: isOccupied ? 0.6 : 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem'
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    disabled={!isSelectable}
                                                    onChange={() => { }}
                                                    style={{ 
                                                        marginRight: '0.5rem', 
                                                        opacity: isSelectable ? 1 : 0.3 
                                                    }}
                                                />
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                                                    {/* Occupancy visual indicator */}
                                                    <span style={{
                                                        width: '12px',
                                                        height: '12px',
                                                        borderRadius: '50%',
                                                        backgroundColor: isOccupied ? '#f44336' : '#4caf50',
                                                        flexShrink: 0
                                                    }} />
                                                    <div>
                                                        <div style={{ fontWeight: 'bold' }}>{pallet.pallet_code}</div>
                                                        <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                                                            Occupied • {isOccupied ? 'True' : 'False'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                }
                            </div>
                        )}
                    </div>
                </div>

                {/* Bulk Edit Form */}
                {selectedPalletIds.length > 0 && (
                    <div style={{ marginBottom: '1.5rem' }}>
                        <h4 style={{ marginBottom: '1rem', color: color.fg }}>Edit Selected Pallets</h4>

                        <div style={{ display: 'grid', gap: '1rem' }}>

                            {/* Pallet Type */}
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>
                                    Pallet Type: <span style={{ color: '#d32f2f' }}>*</span>
                                </label>
                                <select
                                    value={bulkEditForm.pallet_type}
                                    onChange={(e) => setBulkEditForm(prev => ({ ...prev, pallet_type: e.target.value }))}
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem',
                                        border: `1px solid ${color.border}`,
                                        borderRadius: '4px',
                                        backgroundColor: color.bg,
                                        color: color.fg
                                    }}
                                >
                                    <option value="">Select Pallet Type</option>
                                    <option value="standard">Standard</option>
                                    <option value="mixed_sku">Mixed SKU</option>
                                    <option value="single_sku">Single SKU</option>
                                </select>
                            </div>

                            {/* Status */}
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>
                                    Status (optional):
                                </label>
                                <select
                                    value={bulkEditForm.status}
                                    onChange={(e) => setBulkEditForm(prev => ({ ...prev, status: e.target.value }))}
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem',
                                        border: `1px solid ${color.border}`,
                                        borderRadius: '4px',
                                        backgroundColor: color.bg,
                                        color: color.fg
                                    }}
                                >
                                    <option value="">Keep Current Status</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>

                        </div>
                    </div>
                )}

                {/* Footer */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '1rem',
                    marginTop: '1rem'
                }}>
                    <button
                        onClick={closeModal}
                        style={{
                            padding: '0.75rem 1.5rem',
                            border: `1px solid ${color.border}`,
                            borderRadius: '4px',
                            backgroundColor: color.bg,
                            color: color.fg,
                            cursor: 'pointer'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleBulkDelete}
                        disabled={selectedPalletIds.length === 0 || isBulkDeleting}
                        style={{
                            padding: '0.75rem 1.5rem',
                            border: 'none',
                            borderRadius: '4px',
                            backgroundColor: selectedPalletIds.length > 0 && !isBulkDeleting ?  color.danger : '#6c757d',
                            color: '#000',
                            cursor: selectedPalletIds.length > 0 && !isBulkDeleting ? 'pointer' : 'not-allowed',
                            fontWeight: 'bold'
                        }}
                    >
                        {isBulkDeleting ? 'Deleting...' : `Delete ${selectedPalletIds.length} Pallet${selectedPalletIds.length !== 1 ? 's' : ''}`}
                    </button>
                    <button
                        onClick={handleBulkEdit}
                        disabled={selectedPalletIds.length === 0 || isBulkEditing}
                        style={{
                            padding: '0.75rem 1.5rem',
                            border: 'none',
                            borderRadius: '4px',
                            backgroundColor: selectedPalletIds.length > 0 && !isBulkEditing ? color.warning : '#6c757d',
                            color: '#000',
                            cursor: selectedPalletIds.length > 0 && !isBulkEditing ? 'pointer' : 'not-allowed',
                            fontWeight: 'bold'
                        }}
                    >
                        {isBulkEditing ? 'Updating...' : `Update ${selectedPalletIds.length} Pallet${selectedPalletIds.length !== 1 ? 's' : ''}`}
                    </button>
                </div>
            </div>
        </div>
    );
}

BulkEditPalletsModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    warehouseLanes: PropTypes.object,
    allPalletsData: PropTypes.array, // New pallet structure with occupancy data
    allCustomers: PropTypes.array,
    userProfile: PropTypes.object,
    triggerRefresh: PropTypes.func.isRequired,
    onRefreshAllPallets: PropTypes.func, // Callback to refresh all pallets data
    theme: PropTypes.string
};